/**
 * The summon-mode pill's words, at two widths (2026-09-13).
 *
 * The pill names, in words, whether Prest0n answers every message in a shared
 * thread or only when he is mentioned — it exists because the @ toggle beside
 * it is an icon, and an icon has no ambient state (owner feedback 2026-08-07).
 * Both states must therefore stay visible; neither may be dropped on a phone
 * to save a line.
 *
 * What CAN go is the subject. "Prest0n replies only when mentioned" is 35
 * characters sitting directly under a header line that already says Prest0n,
 * inside a column narrower than the sentence, so at 390px the clip cut it
 * mid-word rather than wrapping — the "awkward position" in the owner's
 * screenshot. Dropping the redundant subject halves it and it fits.
 *
 * The long form stays the accessible name and the tooltip, so nothing is lost
 * to a screen reader or a long press.
 *
 * The ERROR state is exempt at every width: a rejected setting write is the
 * one message here the reader cannot reconstruct from context, and the notice
 * is written by the code that failed, not by this table.
 */

export type SummonPillState = 'required' | 'open';

interface SummonPillCopy {
  /** The full sentence — the accessible name, and the tooltip on a phone. */
  full: string;
  /** The phone line. Never longer than the full form. */
  short: string;
}

const COPY: Record<'en' | 'es', Record<SummonPillState, SummonPillCopy>> = {
  en: {
    required: {
      full: 'Prest0n replies only when mentioned',
      short: 'only when mentioned',
    },
    open: {
      full: 'Prest0n replies to every message',
      short: 'replies to everyone',
    },
  },
  es: {
    required: {
      full: 'Prest0n solo responde cuando lo mencionan',
      short: 'solo si lo mencionan',
    },
    open: {
      full: 'Prest0n responde a todos los mensajes',
      short: 'responde a todos',
    },
  },
};

export function summonPillCopy(lang: string, state: SummonPillState): SummonPillCopy {
  return COPY[lang === 'es' ? 'es' : 'en'][state];
}

export function summonPillState(requireMention: boolean | undefined): SummonPillState {
  // `undefined` reads as required, which is the toggle's own default reading
  // (`requireMention !== false`) — kept identical so the pill and the toggle
  // can never name different modes for the same thread.
  return requireMention !== false ? 'required' : 'open';
}
